import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { formatDMY } from "@/lib/dmy";
import {
  Package, Printer, Download, RefreshCw, CheckCircle2, AlertTriangle,
  Layers, Users, BarChart3, FileDown, FileSpreadsheet, ChevronDown,
  ShieldCheck, FileText, XCircle, Upload, AlertCircle, X, Shuffle, Unlink,
  ArrowUpCircle, ArrowDownCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (val) => {
  if (!val) return "₹0";
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)}L`;
  return `₹${val.toLocaleString("en-IN")}`;
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl border border-slate-700">
      <p className="font-semibold">{d.label}</p>
      <p className="text-blue-300">{d.parcels} parcels</p>
      <p className="text-emerald-300">{fmt(d.total_cost)}</p>
    </div>
  );
};

const MonthDetailReportPage = () => {
  const [searchParams] = useSearchParams();
  const year = parseInt(searchParams.get("year") || new Date().getFullYear());
  const month = parseInt(searchParams.get("month") || new Date().getMonth() + 1);
  usePageTitle(`Month Detail — ${month}/${year}`);

  const { token, user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportSections, setExportSections] = useState({
    summary: true, sheets: true, clients: true, daily: false,
    high_alerts: true, low_alerts: true, cross_month: false, packing_mismatch: false, errors: true, upload_history: false
  });

  const openSectionTab = (section) => {
    window.open(`/reports/section-print?year=${year}&month=${month}&section=${section}`, "_blank");
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/month-detail?year=${year}&month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to fetch");
      setData(await res.json());
    } catch (e) {
      toast.error("Failed: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token, year, month]);

  const handleMarkComplete = async () => {
    setVerifying(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/pending-shipment-costing/mark-complete`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ year_month: `${year}-${String(month).padStart(2, '0')}`, notes: verifyNotes })
      });
      if (!res.ok) throw new Error("Failed");
      toast.success(`${data?.month_name} ${year} marked as verified`);
      setVerifyDialog(false);
      setVerifyNotes("");
      fetchData();
    } catch (e) {
      toast.error("Failed: " + e.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleUnmark = async () => {
    try {
      const ym = `${year}-${String(month).padStart(2, '0')}`;
      const res = await fetch(`${BACKEND_URL}/api/reports/pending-shipment-costing/unmark-complete?year_month=${ym}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed");
      toast.success("Verification removed");
      fetchData();
    } catch (e) { toast.error("Failed: " + e.message); }
  };

  const exportToPDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    doc.setFontSize(14);
    doc.text(`${data.month_name} ${data.year} — Shipment Costing Detail`, pw / 2, 16, { align: "center" });
    doc.setFontSize(8); doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pw / 2, 22, { align: "center" });
    let y = 30;
    const sm = data.summary;
    const addTable = (title, head, body) => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFontSize(10); doc.setTextColor(40); doc.text(title, 14, y);
      doc.autoTable({ startY: y + 3, head: [head], body, theme: "striped", headStyles: { fillColor: [51,65,85], fontSize: 7 }, styles: { fontSize: 6 } });
      y = doc.lastAutoTable.finalY + 8;
    };
    if (exportSections.summary) {
      addTable("Summary", ["Metric","Value"], [["Parcels",sm.total_parcels],["Packing",`${sm.packing_pct}%`],["Costing",`${sm.costing_pct}%`],["Errors",sm.with_errors],["Total Cost",fmt(sm.total_cost)]]);
    }
    if (exportSections.sheets) {
      addTable("Sheet Breakdown", ["Sheet","Parcels","Packing%","Errors","Cost"], data.sheets.map(s => [s.name,s.parcel_count,`${s.packing_pct}%`,s.with_errors,fmt(s.total_cost)]));
    }
    if (exportSections.clients) {
      addTable("Client Breakdown", ["Client","Parcels","Cost"], data.clients.map(c => [c.code,c.parcels,fmt(c.total_cost)]));
    }
    if (exportSections.high_alerts && data.price_alerts?.high?.length) {
      addTable("High Value Alerts", ["Tracking","Client","Product","Qty","Total","Flags"], data.price_alerts.high.map(o => [o.tracking,o.client,o.product,o.qty,fmt(o.total),(o.flags||[]).join(", ")]));
    }
    if (exportSections.low_alerts && data.price_alerts?.low?.length) {
      addTable("Low Value Alerts", ["Tracking","Client","Product","Qty","Total","Flags"], data.price_alerts.low.slice(0,50).map(o => [o.tracking,o.client,o.product,o.qty,fmt(o.total),(o.flags||[]).join(", ")]));
    }
    if (exportSections.errors && data.error_details?.length) {
      addTable("Errors", ["Tracking","Product","Client","Error"], data.error_details.map(e => [e.tracking,e.product,e.client,(e.errors||[]).join(", ")]));
    }
    if (exportSections.cross_month && data.cross_month_orders?.total) {
      const rows = [];
      data.cross_month_orders.months.forEach(cm => cm.orders.forEach(o => rows.push([cm.year_month,o.tracking,o.product,o.client,o.date])));
      addTable("Cross-Month Orders", ["Month","Tracking","Product","Client","Date"], rows);
    }
    doc.save(`${data.month_name}-${data.year}-detail.pdf`);
    toast.success("PDF exported"); setShowExportDialog(false);
  };

  const exportToCSV = () => {
    if (!data) return;
    let csv = `${data.month_name} ${data.year} Detail Report\n`;
    if (exportSections.sheets) {
      csv += `\nSheet Breakdown\nSheet,Parcels,Packing%,Costed,Errors,TotalCost\n`;
      data.sheets.forEach(s => { csv += `${s.name},${s.parcel_count},${s.packing_pct},${s.with_costing},${s.with_errors},${s.total_cost}\n`; });
    }
    if (exportSections.clients) {
      csv += `\nClient Breakdown\nClient,Parcels,TotalCost\n`;
      data.clients.forEach(c => { csv += `${c.code},${c.parcels},${c.total_cost}\n`; });
    }
    if (exportSections.high_alerts && data.price_alerts?.high?.length) {
      csv += `\nHigh Value Alerts\nTracking,Client,Product,Qty,Medicine,Shipping,Total,Flags\n`;
      data.price_alerts.high.forEach(o => { csv += `${o.tracking},${o.client},"${o.product}",${o.qty},${o.medicine},${o.shipping},${o.total},"${(o.flags||[]).join("; ")}"\n`; });
    }
    if (exportSections.low_alerts && data.price_alerts?.low?.length) {
      csv += `\nLow Value Alerts\nTracking,Client,Product,Qty,Medicine,Shipping,Total,Flags\n`;
      data.price_alerts.low.forEach(o => { csv += `${o.tracking},${o.client},"${o.product}",${o.qty},${o.medicine},${o.shipping},${o.total},"${(o.flags||[]).join("; ")}"\n`; });
    }
    if (exportSections.errors && data.error_details?.length) {
      csv += `\nErrors\nTracking,Product,Client,Error\n`;
      data.error_details.forEach(e => (e.errors||[]).forEach(err => { csv += `${e.tracking},"${e.product}",${e.client},"${err}"\n`; }));
    }
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${data.month_name}-${data.year}-detail.csv`; a.click();
    toast.success("CSV exported"); setShowExportDialog(false);
  };

  // Full Download - Simplified export for parcel verification
  // Columns: Client Code, Customer Name, Country, Tracking Number, Product Name, Quantity
  const handleFullDownload = async () => {
    if (!data) {
      toast.error("No data available");
      return;
    }

    try {
      // Fetch full orders for the month from shipment-costing API
      const dateFrom = `01${String(month).padStart(2, '0')}${String(year).slice(-2)}`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo = `${String(lastDay).padStart(2, '0')}${String(month).padStart(2, '0')}${String(year).slice(-2)}`;
      
      toast.info("Fetching all orders for the month...");
      
      const response = await fetch(
        `${BACKEND_URL}/api/shipment-costing/orders?date_from=${dateFrom}&date_to=${dateTo}&page_size=50000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }
      
      const ordersData = await response.json();
      const orders = ordersData.orders || [];
      
      if (orders.length === 0) {
        toast.error("No orders found for this month");
        return;
      }

      const wb = XLSX.utils.book_new();
      
      // Prepare data with simplified columns
      const wsData = [
        [`PARCEL VERIFICATION REPORT - ${data.month_name} ${data.year}`],
        [`Period: ${data.month_name} 01 to ${lastDay}, ${data.year}`],
        [`Total Parcels: ${orders.length}`],
        [],
        ["Sr No", "Client Code", "Customer Name", "Country", "Tracking Number", "Product Name", "Quantity"]
      ];

      // Add data rows
      orders.forEach((o, idx) => {
        // Combine product name and second name if exists
        let productName = o.product || "";
        if (o.second_name) {
          productName = `${productName}${productName ? " + " : ""}${o.second_name}`;
        }
        
        // Combine quantities - ONLY use strips (qty field), not pills (quantity field)
        let qtyValue = o.qty || "";
        
        wsData.push([
          idx + 1,
          o.client_code || "",
          o.customer_name || o.address?.split(',')[0]?.trim() || "",
          o.detected_country || "",
          o.tracking || "",
          productName,
          qtyValue
        ]);
      });

      // Add summary at the end
      wsData.push([]);
      wsData.push(["", "", "", "", "", "TOTAL PARCELS:", orders.length]);
      
      // Count by client
      const clientCounts = {};
      orders.forEach(o => {
        const client = o.client_code || "Unknown";
        clientCounts[client] = (clientCounts[client] || 0) + 1;
      });
      
      wsData.push([]);
      wsData.push(["CLIENT SUMMARY"]);
      wsData.push(["Client Code", "Parcel Count"]);
      Object.entries(clientCounts)
        .sort((a, b) => b[1] - a[1])
        .forEach(([client, count]) => {
          wsData.push([client, count]);
        });

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Set column widths
      ws["!cols"] = [
        { wch: 8 },   // Sr No
        { wch: 12 },  // Client Code
        { wch: 35 },  // Customer Name
        { wch: 15 },  // Country
        { wch: 20 },  // Tracking Number
        { wch: 40 },  // Product Name
        { wch: 15 }   // Quantity
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Parcel Verification");
      
      const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      
      const filename = `parcel_verification_${data.month_name}_${data.year}.xlsx`;
      saveAs(blob, filename);
      
      toast.success(`Full download: ${orders.length} parcels exported for verification`);
      setShowExportDialog(false);
    } catch (error) {
      console.error("Full download error:", error);
      toast.error("Failed to export: " + error.message);
    }
  };

  const SectionActions = ({ sectionKey }) => (
    <div className="flex items-center gap-1 print:hidden">
      <button onClick={() => openSectionTab(sectionKey)} className="text-[10px] px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors" title="Open in new tab">Open ↗</button>
    </div>
  );

  if (loading) return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-10 w-80" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div>
      <Skeleton className="h-48" />
    </div>
  );

  if (!data) return <div className="p-6 text-center text-slate-500">No data available</div>;

  const sm = data.summary;
  const fs = data.file_status || {};
  const maxParcels = Math.max(...(data.daily_breakdown?.map(d => d.parcels) || [1]), 1);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-7xl mx-auto print:p-2" data-testid="month-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-blue-600" />
            {data.month_name} {data.year}
          </h1>
          <p className="text-sm text-slate-500">Shipment Costing Detail Report</p>
          {data.below_threshold && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Below {data.parcel_threshold} parcel threshold — data may be incomplete
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Admin Mark as Verified */}
          {isAdmin && !data.verification && sm.total_parcels > 0 && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setVerifyDialog(true); setVerifyNotes(""); }} data-testid="mark-verified-btn">
              <ShieldCheck className="w-4 h-4 mr-1" />Mark as Verified
            </Button>
          )}
          {isAdmin && data.verification && (
            <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={handleUnmark} data-testid="unmark-verified-btn">
              <XCircle className="w-4 h-4 mr-1" />Remove Verification
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => setShowExportDialog(true)} data-testid="export-dialog-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
        </div>
      </div>

      {/* Verification Banner */}
      {data.verification && (
        <div className="flex items-center gap-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <ShieldCheck className="w-5 h-5 text-blue-600 shrink-0" />
          <div className="text-sm">
            <span className="font-medium text-blue-800">Verified</span>
            <span className="text-blue-600"> by {data.verification.verified_by} on {formatDMY(data.verification.verified_at)}</span>
            {data.verification.notes && <span className="text-blue-500"> — {data.verification.notes}</span>}
          </div>
        </div>
      )}

      {/* File Upload Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className={`flex items-start gap-3 p-3 rounded-lg border ${fs.order_uploaded ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <Upload className={`w-5 h-5 mt-0.5 shrink-0 ${fs.order_uploaded ? 'text-green-600' : 'text-red-500'}`} />
          <div>
            <p className={`text-sm font-medium ${fs.order_uploaded ? 'text-green-800' : 'text-red-800'}`}>
              Order Sheet: {fs.order_uploaded ? 'Uploaded' : 'Not Uploaded'}
            </p>
            {fs.order_files?.length > 0 && (
              <p className="text-xs text-green-600 mt-0.5 font-mono">{fs.order_files.join(", ")}</p>
            )}
            {fs.order_uploaded && !fs.order_files?.length && (
              <p className="text-xs text-green-600 mt-0.5">{sm.total_parcels} parcels in database</p>
            )}
          </div>
        </div>
        <div className={`flex items-start gap-3 p-3 rounded-lg border ${fs.packing_uploaded || fs.packing_merged ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <Upload className={`w-5 h-5 mt-0.5 shrink-0 ${fs.packing_uploaded || fs.packing_merged ? 'text-green-600' : 'text-red-500'}`} />
          <div>
            <p className={`text-sm font-medium ${fs.packing_uploaded || fs.packing_merged ? 'text-green-800' : 'text-red-800'}`}>
              Packing Sheet: {fs.packing_uploaded ? 'Uploaded' : fs.packing_merged ? 'Merged via Weight Data' : 'Not Uploaded'}
            </p>
            {fs.packing_files?.length > 0 && (
              <p className="text-xs text-green-600 mt-0.5 font-mono">{fs.packing_files.join(", ")}</p>
            )}
            {!fs.packing_uploaded && !fs.packing_merged && (
              <p className="text-xs text-red-600 mt-0.5">Packing data not found for this month</p>
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-3">
            <p className="text-xs text-blue-600">Total Parcels</p>
            <p className="text-2xl font-bold text-blue-700">{sm.total_parcels.toLocaleString()}</p>
            {data.below_threshold && <p className="text-[10px] text-amber-600 font-medium">Below {data.parcel_threshold} threshold</p>}
          </CardContent>
        </Card>
        <Card className={`${sm.packing_pct === 100 ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
          <CardContent className="p-3">
            <p className={`text-xs ${sm.packing_pct === 100 ? 'text-green-600' : 'text-amber-600'}`}>Packing Coverage</p>
            <p className={`text-2xl font-bold ${sm.packing_pct === 100 ? 'text-green-700' : 'text-amber-700'}`}>{sm.packing_pct}%</p>
            <p className="text-[10px] text-slate-500">{sm.with_packing}/{sm.total_parcels} parcels</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-3">
            <p className="text-xs text-emerald-600">Total Cost</p>
            <p className="text-2xl font-bold text-emerald-700">{fmt(sm.total_cost)}</p>
            <p className="text-[10px] text-slate-500">Med: {fmt(sm.total_medicine)} | Ship: {fmt(sm.total_shipping)}</p>
          </CardContent>
        </Card>
        {/* Clickable Error Card */}
        <Card
          className={`cursor-pointer transition-all hover:shadow-md ${sm.with_errors > 0 ? 'border-red-200 bg-red-50 hover:border-red-300' : 'border-green-200 bg-green-50'}`}
          onClick={() => sm.with_errors > 0 && setShowErrors(true)}
          data-testid="error-card-clickable"
        >
          <CardContent className="p-3">
            <p className={`text-xs ${sm.with_errors > 0 ? 'text-red-600' : 'text-green-600'}`}>Errors</p>
            <p className={`text-2xl font-bold ${sm.with_errors > 0 ? 'text-red-700' : 'text-green-700'}`}>{sm.with_errors}</p>
            <p className="text-[10px] text-slate-500">
              {sm.with_errors > 0 ? 'Click to view details' : `${sm.costing_pct}% costing done`}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Chart */}
      {data.daily_breakdown?.length > 0 && (
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-600" />Daily Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="px-3 py-2">
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={data.daily_breakdown} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} interval={data.daily_breakdown.length > 15 ? 1 : 0} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="parcels" radius={[3, 3, 0, 0]}>
                  {data.daily_breakdown.map((entry, idx) => (
                    <Cell key={idx} fill={`rgba(59,130,246,${0.35 + (entry.parcels / maxParcels) * 0.65})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Sheet Breakdown */}
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm flex items-center justify-between"><span className="flex items-center gap-2"><Layers className="w-4 h-4 text-indigo-600" />Sheet Breakdown ({data.sheets.length} sheets)</span><SectionActions sectionKey="sheets" /></CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="sheet-breakdown-table">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Sheet</th>
                <th className="px-3 py-2 text-right font-medium">Parcels</th>
                <th className="px-3 py-2 text-right font-medium">Packing</th>
                <th className="px-3 py-2 text-right font-medium hidden md:table-cell">Costed</th>
                <th className="px-3 py-2 text-right font-medium hidden md:table-cell">Errors</th>
                <th className="px-3 py-2 text-right font-medium">Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.sheets.map(s => (
                <tr key={s.name} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-800">{s.name}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.parcel_count}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-10 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${s.packing_pct === 100 ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${s.packing_pct}%` }} />
                      </div>
                      <span className="text-xs font-mono w-8 text-right">{s.packing_pct}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono hidden md:table-cell">{s.with_costing}</td>
                  <td className="px-3 py-2 text-right hidden md:table-cell">
                    {s.with_errors > 0 ? <Badge variant="destructive" className="text-xs">{s.with_errors}</Badge> : <span className="text-green-600 text-xs">0</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{fmt(s.total_cost)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right font-mono">{sm.total_parcels}</td>
                <td className="px-3 py-2 text-right text-xs">{sm.packing_pct}%</td>
                <td className="px-3 py-2 text-right font-mono hidden md:table-cell">{sm.with_costing}</td>
                <td className="px-3 py-2 text-right hidden md:table-cell">{sm.with_errors}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{fmt(sm.total_cost)}</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Client Breakdown */}
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm flex items-center justify-between"><span className="flex items-center gap-2"><Users className="w-4 h-4 text-violet-600" />Client Breakdown ({data.clients.length} clients)</span><SectionActions sectionKey="clients" /></CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="client-breakdown-table">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Client</th>
                <th className="px-3 py-2 text-right font-medium">Parcels</th>
                <th className="px-3 py-2 text-right font-medium">Total Cost</th>
                <th className="px-3 py-2 text-right font-medium hidden md:table-cell">Share</th>
              </tr>
            </thead>
            <tbody>
              {data.clients.map(c => (
                <tr key={c.code} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-800">{c.code}</td>
                  <td className="px-3 py-2 text-right font-mono">{c.parcels}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{fmt(c.total_cost)}</td>
                  <td className="px-3 py-2 text-right hidden md:table-cell">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-violet-500 rounded-full" style={{ width: `${sm.total_parcels ? (c.parcels / sm.total_parcels * 100) : 0}%` }} />
                      </div>
                      <span className="text-xs text-slate-500 w-8 text-right">{sm.total_parcels ? Math.round(c.parcels / sm.total_parcels * 100) : 0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* High Value Alerts */}
      {data.price_alerts?.high_count > 0 && (
        <Card className="border-orange-200">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2"><ArrowUpCircle className="w-4 h-4 text-orange-600" />High Value Alerts — {data.price_alerts.high_count} parcels above ₹{(data.price_alerts.high_threshold || 50000).toLocaleString()}</span>
              <SectionActions sectionKey="high_alerts" />
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <p className="text-xs text-orange-700 bg-orange-50 px-3 py-1.5 rounded mb-3">
              These parcels have unusually high costing — verify pricing is correct
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="high-value-table">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium text-xs">Tracking</th>
                    <th className="px-2 py-1.5 text-left font-medium text-xs">Client</th>
                    <th className="px-2 py-1.5 text-left font-medium text-xs">Product</th>
                    <th className="px-2 py-1.5 text-right font-medium text-xs">Qty</th>
                    <th className="px-2 py-1.5 text-right font-medium text-xs hidden md:table-cell">Medicine</th>
                    <th className="px-2 py-1.5 text-right font-medium text-xs hidden md:table-cell">Shipping</th>
                    <th className="px-2 py-1.5 text-right font-medium text-xs">Total</th>
                    <th className="px-2 py-1.5 text-left font-medium text-xs">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {data.price_alerts.high.map((o, i) => (
                    <tr key={i} className="border-t border-slate-100 bg-orange-50/30">
                      <td className="px-2 py-1.5 font-mono text-xs text-blue-600">{o.tracking || "-"}</td>
                      <td className="px-2 py-1.5 text-xs font-medium">{o.client}</td>
                      <td className="px-2 py-1.5 text-xs truncate max-w-[200px]">{o.product || "-"}</td>
                      <td className="px-2 py-1.5 text-right text-xs font-mono">{o.qty}</td>
                      <td className="px-2 py-1.5 text-right text-xs font-mono hidden md:table-cell">{fmt(o.medicine)}</td>
                      <td className="px-2 py-1.5 text-right text-xs font-mono hidden md:table-cell">{fmt(o.shipping)}</td>
                      <td className="px-2 py-1.5 text-right text-xs font-mono font-semibold text-orange-700">{fmt(o.total)}</td>
                      <td className="px-2 py-1.5">{o.flags?.map((f, j) => <Badge key={j} className="text-[9px] bg-orange-100 text-orange-700 mr-1">{f}</Badge>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low Value Alerts */}
      {data.price_alerts?.low_count > 0 && (
        <Card className="border-sky-200">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2"><ArrowDownCircle className="w-4 h-4 text-sky-600" />Low Value Alerts — {data.price_alerts.low_count} parcels below ₹{(data.price_alerts.low_threshold || 1000).toLocaleString()}</span>
              <SectionActions sectionKey="low_alerts" />
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <p className="text-xs text-sky-700 bg-sky-50 px-3 py-1.5 rounded mb-3">
              These parcels have unusually low costing — may have missing prices or incorrect data
            </p>
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="low-value-table">
                <thead className="bg-slate-100 text-slate-600 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium text-xs">Tracking</th>
                    <th className="px-2 py-1.5 text-left font-medium text-xs">Client</th>
                    <th className="px-2 py-1.5 text-left font-medium text-xs">Product</th>
                    <th className="px-2 py-1.5 text-right font-medium text-xs">Qty</th>
                    <th className="px-2 py-1.5 text-right font-medium text-xs hidden md:table-cell">Medicine</th>
                    <th className="px-2 py-1.5 text-right font-medium text-xs hidden md:table-cell">Shipping</th>
                    <th className="px-2 py-1.5 text-right font-medium text-xs">Total</th>
                    <th className="px-2 py-1.5 text-left font-medium text-xs">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {data.price_alerts.low.map((o, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 font-mono text-xs text-blue-600">{o.tracking || "-"}</td>
                      <td className="px-2 py-1.5 text-xs font-medium">{o.client}</td>
                      <td className="px-2 py-1.5 text-xs truncate max-w-[200px]">{o.product || "-"}</td>
                      <td className="px-2 py-1.5 text-right text-xs font-mono">{o.qty}</td>
                      <td className="px-2 py-1.5 text-right text-xs font-mono hidden md:table-cell">{fmt(o.medicine)}</td>
                      <td className="px-2 py-1.5 text-right text-xs font-mono hidden md:table-cell">{fmt(o.shipping)}</td>
                      <td className="px-2 py-1.5 text-right text-xs font-mono font-semibold text-sky-700">{fmt(o.total)}</td>
                      <td className="px-2 py-1.5">{o.flags?.map((f, j) => <Badge key={j} className="text-[9px] bg-sky-100 text-sky-700 mr-1">{f}</Badge>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cross-Month Orders */}
      {data.cross_month_orders?.total > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2"><Shuffle className="w-4 h-4 text-amber-600" />Cross-Month Orders ({data.cross_month_orders.total} orders from other months)</span>
              <SectionActions sectionKey="cross_month" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-2">
            <p className="text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded">
              This month's upload batch also contains orders dated in other months
            </p>
            {data.cross_month_orders.months.map((cm) => (
              <div key={cm.year_month} className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-amber-50 flex items-center justify-between">
                  <span className="text-sm font-medium text-amber-800">{cm.year_month} — {cm.count} orders</span>
                  <Badge className="bg-amber-100 text-amber-700 border-amber-300">{cm.count}</Badge>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium text-xs">Tracking</th>
                      <th className="px-3 py-1.5 text-left font-medium text-xs">Product</th>
                      <th className="px-3 py-1.5 text-left font-medium text-xs">Client</th>
                      <th className="px-3 py-1.5 text-left font-medium text-xs">Date</th>
                      <th className="px-3 py-1.5 text-left font-medium text-xs hidden md:table-cell">Sheet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cm.orders.slice(0, 20).map((o, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 font-mono text-xs text-blue-600">{o.tracking || "-"}</td>
                        <td className="px-3 py-1.5 text-xs">{o.product || "-"}</td>
                        <td className="px-3 py-1.5 text-xs font-medium">{o.client || "-"}</td>
                        <td className="px-3 py-1.5 text-xs font-mono">{formatDMY(o.date)}</td>
                        <td className="px-3 py-1.5 text-xs text-slate-500 hidden md:table-cell">{o.sheet || "-"}</td>
                      </tr>
                    ))}
                    {cm.orders.length > 20 && (
                      <tr><td colSpan={5} className="px-3 py-1.5 text-xs text-slate-400 text-center">+ {cm.orders.length - 20} more orders</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Packing Mismatch */}
      {data.packing_mismatch?.total > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2"><Unlink className="w-4 h-4 text-red-600" />Packing Mismatch ({data.packing_mismatch.total} unmatched entries)</span>
              <SectionActions sectionKey="packing_mismatch" />
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <p className="text-xs text-red-700 bg-red-50 px-3 py-1.5 rounded mb-3">
              These packing entries have no matching order in the system — tracking number may be wrong or order sheet not yet uploaded
            </p>
            <table className="w-full text-sm" data-testid="packing-mismatch-table">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Tracking</th>
                  <th className="px-3 py-2 text-left font-medium">Products</th>
                  <th className="px-3 py-2 text-left font-medium hidden md:table-cell">Service</th>
                  <th className="px-3 py-2 text-right font-medium hidden md:table-cell">Weight</th>
                  <th className="px-3 py-2 text-left font-medium hidden lg:table-cell">Customer</th>
                </tr>
              </thead>
              <tbody>
                {data.packing_mismatch.entries.map((e, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs text-red-600">{e.tracking}</td>
                    <td className="px-3 py-2 text-xs">{Array.isArray(e.products) ? e.products.join(", ") : e.products || "-"}</td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell">{e.service || "-"}</td>
                    <td className="px-3 py-2 text-xs text-right font-mono hidden md:table-cell">{e.weight ? `${e.weight}g` : "-"}</td>
                    <td className="px-3 py-2 text-xs hidden lg:table-cell">{e.customer || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Upload History — Only this month */}
      {data.upload_history?.length > 0 && (
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center justify-between"><span className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-600" />Upload History (This Month)</span><SectionActions sectionKey="upload_history" /></CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Filename</th>
                  <th className="px-3 py-2 text-left font-medium hidden md:table-cell">Uploaded By</th>
                  <th className="px-3 py-2 text-left font-medium hidden md:table-cell">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.upload_history.map((h, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={h.type === "Order Sheet" ? "bg-blue-50 text-blue-700" : "bg-green-50 text-green-700"}>{h.type}</Badge>
                    </td>
                    <td className="px-3 py-2 text-slate-700 font-mono text-xs truncate max-w-xs">{h.filename || "-"}</td>
                    <td className="px-3 py-2 text-slate-500 hidden md:table-cell">{h.uploaded_by || "-"}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs hidden md:table-cell">{h.uploaded_at ? new Date(h.uploaded_at).toLocaleString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Error Details Dialog */}
      <Dialog open={showErrors} onOpenChange={setShowErrors}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Error Details — {data.month_name} {data.year} ({data.error_details?.length || 0} orders)
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 -mx-2 px-2">
            {data.error_details?.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-600 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Tracking</th>
                    <th className="px-2 py-1.5 text-left font-medium">Product</th>
                    <th className="px-2 py-1.5 text-left font-medium">Client</th>
                    <th className="px-2 py-1.5 text-left font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {data.error_details.map((e, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 font-mono text-xs text-blue-600">{e.tracking || "-"}</td>
                      <td className="px-2 py-1.5 text-xs">{e.product || "-"}</td>
                      <td className="px-2 py-1.5 text-xs font-medium">{e.client || "-"}</td>
                      <td className="px-2 py-1.5">
                        {e.errors?.map((err, j) => (
                          <Badge key={j} variant="destructive" className="text-[10px] mr-1 mb-0.5">{err}</Badge>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-center text-slate-500 py-8">No error details available</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Verify Dialog */}
      <Dialog open={verifyDialog} onOpenChange={setVerifyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" />Mark as Verified</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Confirm <strong>{data.month_name} {data.year}</strong> uploads are complete and verified.</p>
          <Input placeholder="Notes (optional)" value={verifyNotes} onChange={(e) => setVerifyNotes(e.target.value)} data-testid="verify-notes-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyDialog(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleMarkComplete} disabled={verifying} data-testid="confirm-verify-btn">
              {verifying ? "Verifying..." : "Confirm Verify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Selection Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-blue-600" />Export Report</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Select sections to include:</p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {[
              { key: "summary", label: "Summary Cards" },
              { key: "sheets", label: "Sheet Breakdown" },
              { key: "clients", label: "Client Breakdown" },
              { key: "high_alerts", label: `High Value Alerts (${data?.price_alerts?.high_count || 0})` },
              { key: "low_alerts", label: `Low Value Alerts (${data?.price_alerts?.low_count || 0})` },
              { key: "errors", label: `Error Details (${data?.error_details?.length || 0})` },
              { key: "cross_month", label: `Cross-Month Orders (${data?.cross_month_orders?.total || 0})` },
              { key: "packing_mismatch", label: `Packing Mismatch (${data?.packing_mismatch?.total || 0})` },
              { key: "upload_history", label: "Upload History" },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={exportSections[key] || false} onChange={() => setExportSections(p => ({ ...p, [key]: !p[key] }))}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-slate-700">{label}</span>
              </label>
            ))}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowExportDialog(false)}>Cancel</Button>
            <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" />Print All</Button>
            <Button variant="outline" size="sm" onClick={exportToCSV}><FileSpreadsheet className="w-4 h-4 mr-1" />CSV</Button>
            <Button size="sm" onClick={exportToPDF}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
          </DialogFooter>
          {/* Full Download Section */}
          <div className="border-t pt-3 mt-2">
            <p className="text-xs text-slate-500 mb-2">Parcel Verification (Full Export):</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleFullDownload}
              className="w-full border-purple-300 hover:bg-purple-50 text-purple-700"
              data-testid="full-download-btn"
            >
              <Download className="w-4 h-4 mr-2 text-purple-500" />
              Full Download (Client, Customer, Country, Tracking, Product, Qty)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthDetailReportPage;
